import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

/**
 * White-label branding context.
 *
 * Fallback chain (consumer side): org branding > tenant branding (Sidebar's
 * existing /api/global-config fetch) > Serverli defaults.
 *
 * Lifecycle:
 *   1. On mount: GET /api/public/branding (host-resolved, pre-auth).
 *   2. After login: refreshBranding() pulls /api/platform/organizations/:id/branding
 *      using the authenticated user's organizationId.
 *   3. CSS vars (--brand-primary, --brand-accent, --brand-login-bg) are
 *      injected on document.documentElement alongside existing --accent-*.
 *   4. document.title and favicon <link> are kept in sync.
 *   5. brandCssOverrides string is appended in a managed <style> tag.
 */

const DEFAULT_BRANDING = Object.freeze({
  brandName: 'ServerliCCM',
  brandLogoUrl: null,
  brandFaviconUrl: null,
  brandPrimaryColor: null,
  brandAccentColor: null,
  brandLoginBg: null,
  brandCssOverrides: null,
  poweredByVisible: true,
  customDomain: null,
  isWhiteLabeled: false,
});

const STYLE_TAG_ID = 'org-brand-css-overrides';
const FAVICON_LINK_ID = 'org-brand-favicon';

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  loaded: false,
  refreshBranding: async () => {},
});

function fetchJson(url, opts) {
  return fetch(url, { credentials: 'include', ...opts }).then(r => {
    if (!r.ok) throw new Error(`${url} -> ${r.status}`);
    return r.json();
  });
}

function applyDocumentSideEffects(branding) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // CSS variables — layer on top of existing --accent-* without replacing them.
  if (branding.brandPrimaryColor) root.style.setProperty('--brand-primary', branding.brandPrimaryColor);
  else root.style.removeProperty('--brand-primary');

  if (branding.brandAccentColor) root.style.setProperty('--brand-accent', branding.brandAccentColor);
  else root.style.removeProperty('--brand-accent');

  if (branding.brandLoginBg) root.style.setProperty('--brand-login-bg', branding.brandLoginBg);
  else root.style.removeProperty('--brand-login-bg');

  // <title>
  if (branding.brandName) document.title = branding.brandName;

  // Favicon — replace href on existing <link rel="icon"> or insert managed one.
  if (branding.brandFaviconUrl) {
    let link = document.getElementById(FAVICON_LINK_ID);
    if (!link) {
      link = document.createElement('link');
      link.id = FAVICON_LINK_ID;
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = branding.brandFaviconUrl;
  }

  // CSS overrides — managed style tag, replaced wholesale on each apply.
  let style = document.getElementById(STYLE_TAG_ID);
  if (branding.brandCssOverrides) {
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_TAG_ID;
      document.head.appendChild(style);
    }
    style.textContent = branding.brandCssOverrides;
  } else if (style) {
    style.remove();
  }
}

function mergeBranding(payload) {
  if (!payload) return { ...DEFAULT_BRANDING };
  return {
    ...DEFAULT_BRANDING,
    ...payload,
    isWhiteLabeled: true,
  };
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loaded, setLoaded] = useState(false);
  const lastAppliedRef = useRef(null);

  // Initial host-resolved branding fetch (pre-auth).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJson('/api/public/branding');
        if (cancelled) return;
        const merged = mergeBranding(data?.branding);
        setBranding(merged);
        applyDocumentSideEffects(merged);
        lastAppliedRef.current = merged;
      } catch {
        // Network / parse error — silently keep defaults
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * Pulls org-specific branding using the authenticated user's organizationId.
   * Call after successful login (authStore can dispatch this).
   * If the user has no orgId or the fetch fails, the existing branding is kept
   * (so we don't clobber the host-resolved branding from the public endpoint).
   */
  const refreshBranding = useCallback(async (orgId) => {
    if (!orgId) {
      // No org context — keep whatever the public endpoint returned.
      return branding;
    }
    try {
      const data = await fetchJson(`/api/platform/organizations/${orgId}/branding`);
      const merged = mergeBranding(data?.branding);
      setBranding(merged);
      applyDocumentSideEffects(merged);
      lastAppliedRef.current = merged;
      return merged;
    } catch {
      return branding;
    }
  }, [branding]);

  return (
    <BrandingContext.Provider value={{ branding, loaded, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

export { DEFAULT_BRANDING };
